import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { BREAKPOINTS } from "@constants"
import { debounce, isClientSide } from "@utils"

import "./FilteringGrid.scss"

const CARD_MARGIN_BOTTOM = 30

export const FILTERING_GRID_COLUMNS_AT_BREAKPOINTS = {
  DEFAULT: 2,
  [BREAKPOINTS.L]: 5,
  [BREAKPOINTS.M]: 4,
  [BREAKPOINTS.S]: 3,
}

const FilteringGrid = ({
  allCards = [],
  columnsAtBreakpoints = FILTERING_GRID_COLUMNS_AT_BREAKPOINTS,
  filteredCardKeys = [],
  sortedCardKeys = [],
}) => {
  const gridRef = useRef(null)
  const animationTimeoutRef = useRef(null)
  const [cardHeight, setCardHeight] = useState(220) // Reasonable default
  const [numCols, setNumCols] = useState(columnsAtBreakpoints.DEFAULT)

  const indexedCardKeys = useMemo(() => allCards.map(({ key }) => key), [allCards])

  useEffect(() => {
    if (!isClientSide()) return

    const calculateCardHeight = () => {
      const calculatedCardHeight = Array.from(gridRef.current.children).reduce((acc, item) => {
        if (!item) return acc
        const itemHeight = item.getBoundingClientRect().height
        return itemHeight > acc ? itemHeight : acc
      }, 0)

      if (calculatedCardHeight === cardHeight) return

      setCardHeight(calculatedCardHeight)
    }

    const calculateColumns = () => {
      if (window.innerWidth >= BREAKPOINTS.L) {
        setNumCols(columnsAtBreakpoints[BREAKPOINTS.L])
        return
      }
      if (window.innerWidth >= BREAKPOINTS.M) {
        setNumCols(columnsAtBreakpoints[BREAKPOINTS.M])
        return
      }
      if (window.innerWidth >= BREAKPOINTS.S) {
        setNumCols(columnsAtBreakpoints[BREAKPOINTS.S])
        return
      }
      setNumCols(columnsAtBreakpoints.DEFAULT)
    }

    const calculateDimensions = debounce(() => {
      calculateColumns()

      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current)
      animationTimeoutRef.current = setTimeout(calculateCardHeight, 400)
    }, 500)

    window.addEventListener('resize', calculateDimensions)

    calculateDimensions()

    return () => {
      window.removeEventListener('resize', calculateDimensions)
    }
  }, [cardHeight, columnsAtBreakpoints])

  return (
    <div className="FilteringGrid">
      <div
        ref={gridRef}
        className="FilteringGrid--Grid"
        style={{
          height:
            filteredCardKeys.length === 0 ?
              `calc(var(--line-height--heading) * var(--text-size--h4) + var(--spacing--S) * 2)` :
              `${Math.ceil(filteredCardKeys.length / numCols) * cardHeight + (Math.ceil(filteredCardKeys.length / numCols) - 1) * CARD_MARGIN_BOTTOM}px`,
        }}
      >
        {filteredCardKeys.length === 0 && (
          <h4 className="no-results">Sorry, no results found.</h4>
        )}
        {allCards.map((card, i) => {
          const indexedKeysToUse = sortedCardKeys.length > 0 ? sortedCardKeys : indexedCardKeys
          const sortedIndex = indexedKeysToUse.indexOf(card.key)
          const activeIndex = indexedKeysToUse.filter(k => filteredCardKeys.includes(k)).indexOf(card.key)
          const isActive = activeIndex > -1
          const indexToUse = isActive ? activeIndex : sortedIndex
          const colIndex = indexToUse % numCols
          const rowIndex = Math.floor(indexToUse / numCols)
          return (
            <div
              className={`FilteringGrid--Card${isActive ? ' is-active' : ""}`}
              key={card.key}
              style={{
                opacity: isActive ? 1 : 0,
                pointerEvents: isActive ? undefined : 'none',
                /* Some magic here:
                /* - Calculate the positioning based on the index that should be used
                /* - If the card is active then position it at the top,
                /*   otherwise put it in the default position.
                /* - Includes a slight vertical offset of +30px when inactive to create
                /*   a pleasing return animation
                */
                transform: `translate3d(${(colIndex) * 100
                  }%, ${rowIndex * cardHeight + (rowIndex * CARD_MARGIN_BOTTOM)
                  }px, 0)`,
                width: `${100 / numCols}%`,
              }}
            >
              {card.render(isActive)}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const CardPropType = {
  key: PropTypes.string.isRequired,
  render: PropTypes.func.isRequired,
}

FilteringGrid.propTypes = {
  allCards: PropTypes.arrayOf(PropTypes.shape(CardPropType)),
  cardHeights: PropTypes.shape({
    DEFAULT: PropTypes.number.isRequired,
    [BREAKPOINTS.L]: PropTypes.number.isRequired,
    [BREAKPOINTS.M]: PropTypes.number.isRequired,
    [BREAKPOINTS.S]: PropTypes.number.isRequired,
  }),
  columnsAtBreakpoints: PropTypes.shape({
    DEFAULT: PropTypes.number.isRequired,
    [BREAKPOINTS.L]: PropTypes.number.isRequired,
    [BREAKPOINTS.M]: PropTypes.number.isRequired,
    [BREAKPOINTS.S]: PropTypes.number.isRequired,
  }),
  filteredCardKeys: PropTypes.arrayOf(PropTypes.string),
  sortedCardKeys: PropTypes.arrayOf(PropTypes.string),
}

export default FilteringGrid
